import { useState, useEffect } from "react";
import api from "@/lib/api";
import { fmtMontant } from "@/lib/format";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Button } from "@/components/ui/button";
import { ChevronsUpDown, Check } from "lucide-react";

export function NoteDebitCombobox({ value, onSelect, selectedLabel = "", disabled = false, testId = "field-note-debit" }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [label, setLabel] = useState(selectedLabel);

  useEffect(() => { setLabel(selectedLabel); }, [selectedLabel]);

  useEffect(() => {
    if (!open) return undefined;
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/notes-debit", { params: { q: query.trim() || undefined, page: 1, page_size: 30 } });
        setResults(data.items);
      } catch {
        setResults([]);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [query, open]);

  const choose = (n) => {
    setLabel(`${n.numero} — ${n.redevable}`);
    onSelect(n);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" role="combobox" aria-expanded={open} disabled={disabled}
          className="w-full justify-between font-normal" data-testid={testId}>
          <span className={label ? "truncate" : "truncate text-slate-400"}>{label || "Rechercher une note de perception…"}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput placeholder="N° de note, redevable, code ou taxation…" value={query} onValueChange={setQuery} data-testid="note-debit-combobox-input" />
          <CommandList>
            <CommandEmpty>Aucune note trouvée</CommandEmpty>
            <CommandGroup>
              {results.map((n) => (
                <CommandItem key={n.id} value={n.id} onSelect={() => choose(n)} data-testid="note-debit-option">
                  <Check className={`mr-2 h-4 w-4 ${n.id === value ? "opacity-100" : "opacity-0"}`} />
                  <span className="mr-2 font-mono text-xs text-slate-500">{n.numero}</span>
                  <span className="truncate">{n.redevable}</span>
                  <span className="ml-auto pl-2 font-mono text-xs text-slate-500">{fmtMontant(n.montant, n.monnaie)}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
